/* eslint-disable jsx-a11y/click-events-have-key-events */

'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Padding, Typography } from '@musinsa/mds';
import { Tooltip } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { FloatingButtonGroup, ScrollToTopButton } from '../common/components';
import { curatorQueries } from '../query-factory';
import { SNOWPLOW_SCHEME } from '../snowplow';
import { DraggableSelectedProductList } from './DraggableSelectedProductList';
import type { ShopCurationEditFormData } from './ShopGoodsCurationEdit';
import type { CurationFormGoodsItem } from './type';

interface Props {
  selectedProducts: CurationFormGoodsItem[];
  onUnselect: (goodsNo: string) => void;
  onSubmit: () => void;
  onDragEnd?: (items: CurationFormGoodsItem[]) => void;
  className?: string;
  analyticsCurationType?: 'goods' | 'snap';
}
export const SelectedWithButton = ({
  selectedProducts,
  onUnselect,
  onSubmit,
  onDragEnd,
  className,
  analyticsCurationType = 'goods',
}: Props) => {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';
  const { setValue } = useFormContext<ShopCurationEditFormData>();

  const handleDragEnd = useCallback(
    (items: CurationFormGoodsItem[]) => {
      onDragEnd?.(items);
    },
    [onDragEnd],
  );

  const handleSubmit = useCallback(() => {
    setValue('selectedProducts', selectedProducts);

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_select_product',
          section_name: 'CurationCreation',
          section_title: 'CurationCreation',
          button_id: 'select_product',
          button_name: '상품 선택하기',
          position: -1,
          item_no_list: selectedProducts.map((product) => product.goodsNo).join(','),
        },
      });
    }

    if (service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'goods_add_complete',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_curation_create',
        type: analyticsCurationType,
      });
    }

    onSubmit();
  }, [analyticsCurationType, curatorId, selectedProducts, service, setValue, onSubmit]);

  return (
    <div
      className={twMerge(
        'sticky bottom-0 left-0 right-0 z-50 max-w-[600px] mx-auto w-full',
        'bg-white',
        className,
      )}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {selectedProducts.length > 1 && (
        <Tooltip
          className="!absolute left-[80px] -top-[25px] !max-w-none"
          align="top left"
          content="드래그하여 순서를 변경할 수 있습니다."
          duration={5000}
          maxCount={1}
        />
      )}

      <FloatingButtonGroup.Container
        className="absolute -top-12 right-0"
        style={{
          // NOTE: translate-y-full 클래스가 적용되지 않음
          transform: 'translateY(-100%)',
        }}
      >
        <ScrollToTopButton />
      </FloatingButtonGroup.Container>

      <DraggableSelectedProductList
        selectedProducts={selectedProducts}
        onUnselect={onUnselect}
        onDragEnd={handleDragEnd}
      />

      <div
        className="relative bg-white safe-area-bottom"
        style={{ borderTop: '1px solid rgba(138, 138, 138, 0.1)' }}
      >
        <Padding x="4" y="3">
          <Button className="w-full" onClick={handleSubmit}>
            <Typography variant="body14pxReg">
              {selectedProducts.length > 0 ? `${selectedProducts.length}개` : ''} 선택 완료하기
            </Typography>
          </Button>
        </Padding>
      </div>
    </div>
  );
};
